// EP-135 Tier 2 — Notice create modal. Admin+ only; the page
// checks role before opening. On save, the controller materialises
// one NoticeDelivery row per audience member at the meeting
// (committee-only for committee meetings; every active OwnerContract
// for AGM/EGM). The channel selects how the audience was notified
// — the operator picks LIFF (in-app inbox) / LINE / email / post.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button, Textarea, Select } from '@/components/ui'
import { useCreateNotice } from '@/hooks/useGovernance'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { NoticeChannel } from '@/types/governance'

export interface NoticeFormModalProps {
  isOpen: boolean
  meetingId: number
  onClose: () => void
}

export function NoticeFormModal({ isOpen, meetingId, onClose }: NoticeFormModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [body, setBody] = useState('')
  const [channel, setChannel] = useState<NoticeChannel>('liff')

  const create = useCreateNotice(meetingId)

  const handleSave = async () => {
    if (!body.trim()) return
    try {
      await create.mutateAsync({
        body: body.trim(),
        channel,
      })
      setBody('')
      setChannel('liff')
      addToast({ type: 'success', message: t('meetings.messages.notice_created') })
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('meetings.actions.new_notice')}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!body.trim()}
            loading={create.isPending}
            data-testid="btn-save-notice"
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label={t('notices.labels.body')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          required
          data-testid="input-notice-body"
        />
        <Select
          label={t('notices.labels.channel')}
          value={channel}
          onChange={(e) => setChannel(e.target.value as NoticeChannel)}
          options={[
            { value: 'liff', label: t('notices.channels.liff') },
            { value: 'line', label: t('notices.channels.line') },
            { value: 'email', label: t('notices.channels.email') },
            { value: 'post', label: t('notices.channels.post') },
          ]}
          data-testid="select-notice-channel"
        />
        <p className="text-xs text-base-content/60 leading-relaxed">
          {t('notices.help.materialise')}
        </p>
      </div>
    </Modal>
  )
}
